<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  icon: { type: String, required: true },
  iconBgColor: { type: String, required: true },
  iconTextColor: { type: String, required: true },
  selectedSlots: { type: Array, required: true }, // Now can be strings OR objects
  disabledSlots: { type: Array, default: () => [] }, // Can be strings OR objects
  multiSelect: { type: Boolean, default: false },
  selectedColor: { type: String, default: 'purple' },
  // New props for enhanced functionality
  isGroupCalendar: { type: Boolean, default: false },
  currentSubjectLevel: { type: Object, default: null }, // { subject_id, level_id, subject_name, level_name }
  showSlotDetails: { type: Boolean, default: true }, // Show "Free" / subject-level info
  hideHeader: { type: Boolean, default: false }, // Hide the header section
  subjects: { type: Array, default: () => [] }, // Selected subjects with level_ids for color assignment
  maxSlotsPerGroup: { type: Number, default: 0 }, // 0 = unlimited; max slots per subject-level combo
  maxTotalSlots: { type: Number, default: 0 }, // 0 = unlimited; max total slots (for 1:1)
})

const emit = defineEmits(['update:selectedSlots'])

// Drag selection state
const isDragging = ref(false)
const dragMode = ref(null) // 'select' or 'deselect'

// Days and time slots configuration
const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const dayLabels = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun'
}

// Extended hours from 8am to 9pm
const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00']
const timeLabels = {
  '08:00': '8 AM',
  '09:00': '9 AM',
  '10:00': '10 AM',
  '11:00': '11 AM',
  '12:00': '12 PM',
  '13:00': '1 PM',
  '14:00': '2 PM',
  '15:00': '3 PM',
  '16:00': '4 PM',
  '17:00': '5 PM',
  '18:00': '6 PM',
  '19:00': '7 PM',
  '20:00': '8 PM',
  '21:00': '9 PM',
}

const buttonColors = computed(() => {
  const colors = {
    purple: {
      selected: 'border-purple-300 bg-purple-100 text-purple-700 font-semibold',
      hover: 'hover:border-purple-300 hover:bg-purple-50'
    },
    yellow: {
      selected: 'border-yellow-300 bg-yellow-100 text-yellow-700 font-semibold',
      hover: 'hover:border-yellow-300 hover:bg-yellow-50'
    }
  }
  return colors[props.selectedColor] || colors.purple
})

// Helper to create slot key from day and time
const createSlotKey = (day, time) => `${day}-${time}`

// Helper to check if a slot (day-time) is disabled
const isSlotDisabled = (day, time) => {
  const slotKey = createSlotKey(day, time)

  // Check if disabled slots contain this day-time combo
  return props.disabledSlots.some(slot => {
    if (typeof slot === 'string') {
      return slot === slotKey
    }
    // If it's an object, check day and time
    return slot.day === day && slot.time === time
  })
}

// Helper to check if a slot is selected
const isSlotSelected = (day, time) => {
  return props.selectedSlots.some(slot => {
    if (typeof slot === 'string') {
      return slot === createSlotKey(day, time)
    }
    // If it's an object, check day and time
    return slot.day === day && slot.time === time
  })
}

// Helper to get slot data if it's selected (for displaying subject/level info)
const getSlotData = (day, time) => {
  return props.selectedSlots.find(slot => {
    if (typeof slot === 'object') {
      return slot.day === day && slot.time === time
    }
    return false
  })
}

// Predefined color palette (30 light colors)
const colorPalette = [
  { border: '#93C5FD', bg: '#DBEAFE', text: '#1E40AF' }, // Blue
  { border: '#86EFAC', bg: '#D1FAE5', text: '#065F46' }, // Green
  { border: '#FCA5A5', bg: '#FEE2E2', text: '#991B1B' }, // Red
  { border: '#FCD34D', bg: '#FEF3C7', text: '#92400E' }, // Yellow
  { border: '#C4B5FD', bg: '#EDE9FE', text: '#5B21B6' }, // Purple
  { border: '#F9A8D4', bg: '#FCE7F3', text: '#9F1239' }, // Pink
  { border: '#67E8F9', bg: '#CFFAFE', text: '#155E75' }, // Cyan
  { border: '#FB923C', bg: '#FED7AA', text: '#9A3412' }, // Orange
  { border: '#A78BFA', bg: '#DDD6FE', text: '#6B21A8' }, // Indigo
  { border: '#34D399', bg: '#D1FAE5', text: '#047857' }, // Emerald
  { border: '#F472B6', bg: '#FAE8FF', text: '#A21CAF' }, // Fuchsia
  { border: '#818CF8', bg: '#E0E7FF', text: '#4338CA' }, // Violet
  { border: '#FDBA74', bg: '#FFEDD5', text: '#C2410C' }, // Amber
  { border: '#2DD4BF', bg: '#CCFBF1', text: '#115E59' }, // Teal
  { border: '#FB7185', bg: '#FFE4E6', text: '#BE123C' }, // Rose
  { border: '#BAE6FD', bg: '#E0F2FE', text: '#0C4A6E' }, // Sky
  { border: '#BBF7D0', bg: '#DCFCE7', text: '#14532D' }, // Lime
  { border: '#FED7AA', bg: '#FFEDD5', text: '#7C2D12' }, // Orange Light
  { border: '#E9D5FF', bg: '#F3E8FF', text: '#6B21A8' }, // Purple Light
  { border: '#FBCFE8', bg: '#FCE7F3', text: '#831843' }, // Pink Light
  { border: '#A5F3FC', bg: '#CFFAFE', text: '#164E63' }, // Cyan Light
  { border: '#FDE68A', bg: '#FEF3C7', text: '#78350F' }, // Yellow Light
  { border: '#BFDBFE', bg: '#DBEAFE', text: '#1E3A8A' }, // Blue Light
  { border: '#6EE7B7', bg: '#D1FAE5', text: '#064E3B' }, // Green Light
  { border: '#FCA5A5', bg: '#FEE2E2', text: '#7F1D1D' }, // Red Light
  { border: '#DDD6FE', bg: '#EDE9FE', text: '#4C1D95' }, // Violet Light
  { border: '#FCD6BB', bg: '#FFEDD5', text: '#9A3412' }, // Peach
  { border: '#C7D2FE', bg: '#E0E7FF', text: '#3730A3' }, // Indigo Light
  { border: '#99F6E4', bg: '#CCFBF1', text: '#134E4A' }, // Teal Light
  { border: '#FECACA', bg: '#FEE2E2', text: '#991B1B' }, // Rose Light
]

// Build a map of subject-level combinations to color indices
const subjectLevelColorMap = computed(() => {
  const map = new Map()
  let colorIndex = 0

  props.subjects.forEach(subject => {
    if (subject.level_ids && subject.level_ids.length > 0) {
      subject.level_ids.forEach(levelId => {
        const key = `${subject.id}-${levelId}`
        map.set(key, colorIndex % colorPalette.length)
        colorIndex++
      })
    }
  })

  return map
})

// Get color for a subject-level combination based on sequential assignment
const getSubjectLevelColor = (subjectId, levelId) => {
  const key = `${subjectId}-${levelId}`
  const colorIndex = subjectLevelColorMap.value.get(key) ?? 0
  return colorPalette[colorIndex]
}

// Get dynamic button styles based on subject-level color
const getButtonStyles = (day, time) => {
  if (!isSlotSelected(day, time)) return {}

  if (props.isGroupCalendar) {
    const slotData = getSlotData(day, time)
    if (slotData && slotData.subject_id && slotData.level_id) {
      const colors = getSubjectLevelColor(slotData.subject_id, slotData.level_id)
      return {
        borderColor: colors.border,
        backgroundColor: colors.bg,
        color: colors.text
      }
    }
  }

  return {}
}

// Slots remaining for the current subject-level combo
const currentGroupSlotsUsed = computed(() => {
  if (!props.isGroupCalendar || !props.currentSubjectLevel || props.maxSlotsPerGroup <= 0) {
    return 0
  }
  return props.selectedSlots.filter(slot =>
    typeof slot === 'object' &&
    slot.subject_id === props.currentSubjectLevel.subject_id &&
    slot.level_id === props.currentSubjectLevel.level_id
  ).length
})

const currentGroupSlotsRemaining = computed(() => {
  if (!props.isGroupCalendar || props.maxSlotsPerGroup <= 0) return Infinity
  return props.maxSlotsPerGroup - currentGroupSlotsUsed.value
})

const isAtGroupLimit = computed(() => {
  return props.isGroupCalendar && props.maxSlotsPerGroup > 0 && currentGroupSlotsRemaining.value <= 0
})

// Total slots limit (for 1:1 sessions)
const isAtTotalLimit = computed(() => {
  return props.maxTotalSlots > 0 && props.selectedSlots.length >= props.maxTotalSlots
})

const handleMouseDown = (day, time, event) => {
  // Don't start drag on disabled slots
  if (isSlotDisabled(day, time)) {
    return
  }

  // For group calendar, require subject-level selection
  if (props.isGroupCalendar && !props.currentSubjectLevel) {
    return
  }

  // Prevent default to avoid text selection
  event.preventDefault()

  isDragging.value = true

  // Determine drag mode: if slot is currently selected, we'll deselect; otherwise select
  dragMode.value = isSlotSelected(day, time) ? 'deselect' : 'select'

  // Apply the action to the first slot
  handleDragSlot(day, time)
}

const handleMouseEnter = (day, time) => {
  if (!isDragging.value) {
    return
  }

  handleDragSlot(day, time)
}

const handleDragSlot = (day, time) => {
  // Don't drag over disabled slots
  if (isSlotDisabled(day, time)) {
    return
  }

  // For group calendar, require subject-level selection
  if (props.isGroupCalendar && !props.currentSubjectLevel) {
    return
  }

  let newSlots = [...props.selectedSlots]

  if (dragMode.value === 'select') {
    // Check if slot already selected
    const alreadySelected = newSlots.some(slot => {
      if (typeof slot === 'string') {
        return slot === createSlotKey(day, time)
      }
      return slot.day === day && slot.time === time
    })

    if (!alreadySelected) {
      // Check max slots per group limit before adding
      if (props.isGroupCalendar && props.currentSubjectLevel && props.maxSlotsPerGroup > 0) {
        const currentCount = newSlots.filter(slot =>
          typeof slot === 'object' &&
          slot.subject_id === props.currentSubjectLevel.subject_id &&
          slot.level_id === props.currentSubjectLevel.level_id
        ).length
        if (currentCount >= props.maxSlotsPerGroup) {
          return
        }
      }

      // Check max total slots limit before adding (for 1:1)
      if (props.maxTotalSlots > 0 && newSlots.length >= props.maxTotalSlots) {
        return
      }

      // Add slot
      if (props.isGroupCalendar && props.currentSubjectLevel) {
        // Add as object with subject/level info (no need to store color, look it up from subjects)
        newSlots.push({
          day,
          time,
          subject_id: props.currentSubjectLevel.subject_id,
          subject_name: props.currentSubjectLevel.subject_name,
          level_id: props.currentSubjectLevel.level_id,
          level_name: props.currentSubjectLevel.level_name,
        })
      } else {
        // Add as simple string
        newSlots.push(createSlotKey(day, time))
      }
    }
  } else {
    // Remove slot
    newSlots = newSlots.filter(slot => {
      if (typeof slot === 'string') {
        return slot !== createSlotKey(day, time)
      }
      return !(slot.day === day && slot.time === time)
    })
  }

  emit('update:selectedSlots', newSlots)
}

const handleMouseUp = () => {
  isDragging.value = false
  dragMode.value = null
}
</script>

<template>
  <div
    @mouseup="handleMouseUp"
    @mouseleave="handleMouseUp"
    class="select-none"
  >
    <div v-if="!hideHeader" class="flex items-center gap-3 mb-4">
      <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', iconBgColor]">
        <span :class="['material-symbols-outlined', iconTextColor]">{{ icon }}</span>
      </div>
      <div>
        <h4 class="font-semibold text-gray-900">{{ title }}</h4>
        <p class="text-xs text-gray-500">{{ description }}</p>
      </div>
    </div>

    <!-- Warning if group calendar and no subject-level selected -->
    <div v-if="isGroupCalendar && !currentSubjectLevel" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="material-symbols-outlined text-yellow-600 text-sm mt-0.5">info</span>
        <p class="text-xs text-yellow-800">Please select a subject and level from the dropdown above to schedule slots</p>
      </div>
    </div>

    <!-- Slots remaining indicator for group calendar -->
    <div v-if="isGroupCalendar && currentSubjectLevel && maxSlotsPerGroup > 0" class="mb-4 p-3 rounded-lg" :class="isAtGroupLimit ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'">
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-sm" :class="isAtGroupLimit ? 'text-orange-600' : 'text-blue-600'">{{ isAtGroupLimit ? 'check_circle' : 'info' }}</span>
        <p class="text-xs" :class="isAtGroupLimit ? 'text-orange-800' : 'text-blue-800'">
          <span class="font-semibold">{{ currentGroupSlotsUsed }}/{{ maxSlotsPerGroup }}</span> session(s) selected for
          <span class="font-semibold">{{ currentSubjectLevel.subject_name }} - {{ currentSubjectLevel.level_name }}</span>
          <span v-if="isAtGroupLimit"> (limit reached)</span>
        </p>
      </div>
    </div>

    <!-- Total slots indicator for non-group calendars (1:1) -->
    <div v-if="!isGroupCalendar && maxTotalSlots > 0" class="mb-4 p-3 rounded-lg" :class="isAtTotalLimit ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50 border border-blue-200'">
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-sm" :class="isAtTotalLimit ? 'text-orange-600' : 'text-blue-600'">{{ isAtTotalLimit ? 'check_circle' : 'info' }}</span>
        <p class="text-xs" :class="isAtTotalLimit ? 'text-orange-800' : 'text-blue-800'">
          <span class="font-semibold">{{ selectedSlots.length }}/{{ maxTotalSlots }}</span> session(s) selected
          <span v-if="isAtTotalLimit"> (limit reached)</span>
        </p>
      </div>
    </div>

    <div class="grid grid-cols-7 gap-2">
      <div v-for="day in daysOfWeek" :key="day" class="text-center">
        <p class="text-xs font-semibold text-gray-600 mb-2">{{ dayLabels[day] }}</p>
        <div class="space-y-1.5">
          <button
            v-for="time in timeSlots"
            :key="time"
            @mousedown="handleMouseDown(day, time, $event)"
            @mouseenter="handleMouseEnter(day, time)"
            :disabled="isSlotDisabled(day, time) || (isGroupCalendar && !currentSubjectLevel)"
            :class="[
              'w-full py-1.5 px-1 text-xs border-2 rounded-lg transition-colors relative',
              isSlotDisabled(day, time)
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : isSlotSelected(day, time) && !isGroupCalendar
                ? buttonColors.selected
                : !isSlotSelected(day, time)
                ? `border-gray-200 ${buttonColors.hover}`
                : '',
              (isGroupCalendar && !currentSubjectLevel) ? 'opacity-50 cursor-not-allowed' : '',
              isSlotSelected(day, time) && isGroupCalendar ? 'font-semibold' : ''
            ]"
            :style="isSlotSelected(day, time) && isGroupCalendar ? getButtonStyles(day, time) : {}"
          >
            <div class="flex flex-col">
              <!-- Time label -->
              <span class="font-medium">{{ timeLabels[time] }}</span>

              <!-- Status/Details line - fixed height to prevent layout shifts -->
              <div
                v-if="showSlotDetails"
                :class="[
                  'text-[10px] mt-0.5 h-6 flex flex-col justify-center',
                  isSlotDisabled(day, time)
                    ? 'text-gray-400'
                    : isSlotSelected(day, time)
                    ? 'opacity-80'
                    : 'text-green-600 font-medium'
                ]"
              >
                <template v-if="isSlotDisabled(day, time)">
                  <div>Unavailable</div>
                </template>
                <template v-else-if="isSlotSelected(day, time)">
                  <template v-if="isGroupCalendar">
                    <div class="truncate uppercase leading-tight" :title="getSlotData(day, time)?.subject_name">{{ getSlotData(day, time)?.subject_name }}</div>
                    <div class="truncate !text-black leading-tight" :title="getSlotData(day, time)?.level_name">{{ getSlotData(day, time)?.level_name }}</div>
                  </template>
                  <template v-else>
                    <div class="hidden md:block">Selected</div>
                    <div class="md:hidden">✔</div>
                  </template>
                </template>
                <template v-else>
                  <div>Free</div>
                </template>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
